import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import uniq from 'lodash/uniq';
import SM from 'services/ServiceManager';
import Service from 'additiv-services';
import AdapterError from 'errors/AdapterError';
import ServerError from 'errors/ServerError';
import { CURRENT_PAGE, PAGE_SIZE } from 'constants/constants';
import { useFormatting } from 'locale';
import { useQueryClient } from '@tanstack/react-query';
import { gql, useLazyQuery } from '@apollo/client';
import { adaptTransactions } from '../adapters/adaptTransactions';

// --- Types ---

interface TransactionParams {
    SecurityType?: string;
    TransactionType?: string;
    Page?: number;
    PageSize?: number;
    From?: string;
    To?: string;
}

interface TransactionsOptions {
    clientId: string | number;
    portfolioId: string | number;
    adaptData?: (data: any, context: any) => any[];
    loadInitially?: boolean;
}

// --- GraphQL ---

const GET_SECURITIES = gql`
    query SearchSecurities($ids: [Int!]) {
        securities(where: { id: { in: $ids } }) {
            items {
                id
                name
                currency {
                  id
                  threeLetterIsoCode
                }
            }
        }
    }
`;

// --- Hook ---

export const useTransactions = (options: TransactionsOptions) => {
    const {
        clientId,
        portfolioId,
        adaptData = adaptTransactions,
        loadInitially = false,
    } = options;

    const { t, i18n: { language } } = useTranslation();
    const { getFormattedDate, getFormattedNumber, getFormattedCurrency } = useFormatting();

    const [getSecurities] = useLazyQuery(GET_SECURITIES, {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
    });
    const queryClient = useQueryClient();

    const [state, setState] = useState<{ data: any[], dataRaw: any, error: any, isLoading: boolean }>({
        data: [],
        dataRaw: {},
        error: null,
        isLoading: false,
    });

    const getTransactions = useCallback(async (args?: {
        optionsParams?: TransactionParams,
        adaptOptions?: any
    }) => {
        const { optionsParams, adaptOptions } = args ?? {};

        const params = {
            SecurityType: optionsParams?.SecurityType || 'Instruments',
            TransactionType: optionsParams?.TransactionType,
            Page: optionsParams?.Page || CURRENT_PAGE,
            PageSize: optionsParams?.PageSize || PAGE_SIZE,
            From: optionsParams?.From,
            To: optionsParams?.To,
        };

        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        try {
            const { raw, securities } = await queryClient.fetchQuery({
                queryKey: ['transactions', clientId, portfolioId, params, language],
                queryFn: async () => {
                    const response = await SM.customClientService('postTransactions', [clientId, portfolioId, params]) as any;
                    let securityIds: number[] = [];

                    try {
                        securityIds = (response?.data?.Transactions || []).map((item: any) => item.SecurityId);
                    } catch (err) {
                        throw new AdapterError(err);
                    }

                    let responseSecurities: any = [];

                    if (securityIds?.length) {
                        responseSecurities = await getSecurities({
                            variables: { ids: uniq(securityIds), language },
                        });
                    }

                    return { raw: response.data, securities: responseSecurities?.data?.securities?.items };
                },
                staleTime: 10 * 60 * 1000,
                gcTime: 20 * 60 * 1000,
            });

            const adapted = adaptData(raw, {
                securities,
                t,
                getFormattedDate,
                getFormattedNumber,
                getFormattedCurrency,
                currency: adaptOptions?.currency,
                baseUrl: adaptOptions?.baseUrl,
            });

            setState({
                data: adapted, dataRaw: raw, error: null, isLoading: false,
            });

            return adapted;
        } catch (err: any) {
            if (Service.isCanceled(err)) {
                setState((prev) => ({ ...prev, isLoading: false }));

                return err;
            }

            const wrapped = err?.type !== undefined ? err : new ServerError(err);

            setState((prev) => ({ ...prev, isLoading: false, error: wrapped }));

            return wrapped;
        }
    }, [
        clientId,
        portfolioId,
        language,
        adaptData,
        t,
        getFormattedDate,
        getFormattedNumber,
        getFormattedCurrency,
        getSecurities,
        queryClient,
    ]);

    // Effects
    useEffect(() => {
        if (loadInitially) {
            getTransactions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadInitially]);

    return {
        data: state.data,
        dataRaw: state.dataRaw,
        error: state.error,
        isLoading: state.isLoading,
        getTransactions,
    };
};
